package com.schoolconnect.app.feature.schoolusers.add

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogColors
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogFormGrid
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogFullNameField
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogLabelSize
import com.schoolconnect.app.designsystem.AppDialogMobileField
import com.schoolconnect.app.designsystem.AppDialogSelectField
import com.schoolconnect.app.designsystem.AppDialogStandardWidth
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogTextField
import com.schoolconnect.app.designsystem.AppDialogValidationSummary
import com.schoolconnect.app.designsystem.AppDialogErrorMessage
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.schoolconnect.app.feature.schoolusers.SchoolUserSchoolPopup
import com.schoolconnect.app.feature.schoolusers.schoolUserGenderLabels
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.close_x
import ischoolconnect.sharedui.generated.resources.info_circle
import ischoolconnect.sharedui.generated.resources.search
import ischoolconnect.sharedui.generated.resources.user_plus
import org.jetbrains.compose.resources.painterResource

private const val StaffIdHelperText =
    "If unavailable, enter a unique temporary ID (e.g., TMP-001)."

private val AddSchoolUserOverlay = Color(0x5A0F172A)
private val AddSchoolUserPrimary = com.schoolconnect.app.designsystem.AppPrimary
private val AddSchoolUserNavy = com.schoolconnect.app.designsystem.AppNavy
private val AddSchoolUserMuted = Color(0xFF536080)
private val AddSchoolUserBorder = com.schoolconnect.app.designsystem.AppBorder
private val AddSchoolUserInputBorder = com.schoolconnect.app.designsystem.AppInputBorder
private val AddSchoolUserDanger = com.schoolconnect.app.designsystem.AppDanger
private val AddSchoolUserSurface = com.schoolconnect.app.designsystem.AppSurface
private val AddSchoolUserDialogColors = com.schoolconnect.app.designsystem.AppDialogDefaultColors


@Composable
fun AddSchoolUserModal(
    open: Boolean,
    onClose: () -> Unit,
    schoolOptions: List<AddSchoolUserSchoolOption> = addSchoolUserSchools,
    isSaving: Boolean = false,
    apiError: String = "",
    onSave: (AddSchoolUserFormValues) -> Unit
) {
    if (!open) return

    var fullName by remember { mutableStateOf("") }
    var staffId by remember { mutableStateOf("") }
    var staffRole by remember { mutableStateOf("") }
    var mobileNumber by remember { mutableStateOf("") }
    var emailAddress by remember { mutableStateOf("") }
    var schoolId by remember { mutableStateOf("") }
    var schoolName by remember { mutableStateOf("") }
    var role by remember { mutableStateOf("") }
    var gender by remember { mutableStateOf("") }
    var status by remember { mutableStateOf("Active") }
    var schoolQuery by remember { mutableStateOf("") }
    var schoolExpanded by remember { mutableStateOf(false) }
    var roleExpanded by remember { mutableStateOf(false) }
    var genderExpanded by remember { mutableStateOf(false) }
    var genderOptions by remember { mutableStateOf(emptyList<String>()) }
    var hasSubmitted by remember { mutableStateOf(false) }
    var localSaving by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        genderOptions = schoolUserGenderLabels()
    }

    val validationErrors = remember(
        hasSubmitted,
        staffId,
        fullName,
        staffRole,
        mobileNumber,
        emailAddress,
        schoolId,
        role,
        gender,
        status
    ) {
        if (hasSubmitted) {
            validateAddSchoolUser(
                AddSchoolUserFormValues(
                    staffId = staffId,
                    fullName = fullName,
                    staffRole = staffRole,
                    mobileNumber = mobileNumber,
                    emailAddress = emailAddress,
                    schoolId = schoolId,
                    role = role,
                    gender = gender,
                    status = status
                )
            )
        } else {
            AddSchoolUserValidationErrors()
        }
    }

    val resolvedSchoolOptions = schoolOptions.ifEmpty { addSchoolUserSchools }
    val selectedSchool = resolvedSchoolOptions.firstOrNull { it.id == schoolId }
    val currentValues = AddSchoolUserFormValues(
        staffId = staffId,
        fullName = fullName,
        staffRole = staffRole,
        mobileNumber = mobileNumber,
        emailAddress = emailAddress,
        schoolId = schoolId,
        role = role,
        gender = gender,
        status = status
    )
    val effectiveSaving = isSaving || localSaving
    // Save stays enabled so an empty form can be submitted once and reveal
    // the form-level summary plus the field-level validation messages.
    val canSave = !effectiveSaving
    val filteredSchools = remember(schoolQuery, resolvedSchoolOptions) {
        val query = schoolQuery.trim()
        if (query.isBlank()) {
            resolvedSchoolOptions
        } else {
            resolvedSchoolOptions.filter { it.name.contains(query, ignoreCase = true) }
        }
    }

    AppDialogScaffold(
        onDismiss = { if (!effectiveSaving) onClose() },
        dismissOnClickOutside = false,
        colors = AddSchoolUserDialogColors,
        isLoading = effectiveSaving,
        loadingTitle = "Adding school user"
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Header(
                onClose = { if (!effectiveSaving) onClose() }
            )
            AppDialogDivider(colors = AddSchoolUserDialogColors)
            AppDialogValidationSummary(
                errors = listOf(
                    validationErrors.staffId,
                    validationErrors.fullName,
                    validationErrors.staffRole,
                    validationErrors.mobileNumber,
                    validationErrors.emailAddress,
                    validationErrors.schoolId,
                    validationErrors.role,
                    validationErrors.gender,
                    validationErrors.status
                ),
                colors = AddSchoolUserDialogColors,
                modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp)
            )
            AppDialogErrorMessage(
                message = apiError,
                colors = AddSchoolUserDialogColors,
                modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp)
            )
            AppDialogFormGrid(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f, fill = false),
                compactBreakpoint = 680.dp
            ) { compact ->
                val scrollState = rememberScrollState()
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .verticalScroll(scrollState)
                        .padding(horizontal = 28.dp, vertical = 24.dp),
                    verticalArrangement = Arrangement.spacedBy(if (compact) 20.dp else 0.dp)
                ) {
                        if (compact) {
                            Column(verticalArrangement = Arrangement.spacedBy(20.dp)) {
                                FieldColumn(
                                    label = "School",
                                    required = true,
                                    field = {
                                        SchoolUserSchoolPopup(
                                            selectedSchool = selectedSchool,
                                            selectedLabel = schoolName.ifBlank { selectedSchool?.name.orEmpty() },
                                            query = schoolQuery,
                                            expanded = schoolExpanded,
                                            filteredSchools = filteredSchools,
                                            onQueryChange = { schoolQuery = it },
                                            onToggleExpanded = { schoolExpanded = !schoolExpanded },
                                            onSchoolSelected = {
                                                schoolId = it.id
                                                schoolName = it.name
                                                schoolQuery = ""
                                                schoolExpanded = false
                                            },
                                            onDismissExpanded = { schoolExpanded = false },
                                            error = validationErrors.schoolId,
                                            fieldTextColor = AddSchoolUserNavy,
                                            mutedTextColor = AddSchoolUserMuted,
                                            primaryColor = AddSchoolUserPrimary,
                                            borderColor = AddSchoolUserInputBorder,
                                            dangerColor = AddSchoolUserDanger,
                                            surfaceColor = AddSchoolUserSurface,
                                            highlightBorderColor = AddSchoolUserBorder
                                        )
                                    }
                                )
                                FieldColumn(
                                    label = "Full Name",
                                    required = true,
                                    field = {
                                        AddUserTextField(
                                            value = fullName,
                                            onValueChange = { fullName = it },
                                            placeholder = "Enter full name",
                                            error = validationErrors.fullName
                                        )
                                    }
                                )
                                FieldColumn(label = "Staff Role", required = true) {
                                    AddUserTextField(value = staffRole, onValueChange = { staffRole = it }, placeholder = "Enter staff role", error = validationErrors.staffRole)
                                }
                                FieldColumn(
                                    label = "Mobile Number",
                                    required = true,
                                    field = {
                                        AddUserTextField(
                                            value = mobileNumber,
                                            onValueChange = { mobileNumber = it },
                                            placeholder = "Enter mobile number",
                                            error = validationErrors.mobileNumber,
                                            keyboardType = androidx.compose.ui.text.input.KeyboardType.Phone
                                        )
                                    }
                                )
                                FieldColumn(
                                    label = "Email Address",
                                    required = true,
                                    field = {
                                        AddUserTextField(
                                            value = emailAddress,
                                            onValueChange = { emailAddress = it },
                                            placeholder = "Enter email address",
                                            error = validationErrors.emailAddress,
                                            keyboardType = androidx.compose.ui.text.input.KeyboardType.Email
                                        )
                                    }
                                )
                                FieldColumn(
                                    label = "Staff ID",
                                    required = true,
                                    helperText = StaffIdHelperText
                                ) {
                                    AddUserTextField(value = staffId, onValueChange = { staffId = it }, placeholder = "Enter staff ID", error = validationErrors.staffId)
                                }
                                FieldColumn(
                                    label = "Role",
                                    required = true,
                                    field = {
                                        AddUserSelectField(
                                            value = role,
                                            placeholder = "Select role",
                                            expanded = roleExpanded,
                                            options = addSchoolUserRoles,
                                            onToggleExpanded = { roleExpanded = !roleExpanded },
                                            onDismissExpanded = { roleExpanded = false },
                                            onOptionSelected = {
                                                role = it
                                                roleExpanded = false
                                            },
                                            error = validationErrors.role
                                        )
                                    }
                                )
                                FieldColumn(label = "Gender", required = true) {
                                    AddUserSelectField(
                                        value = gender,
                                        placeholder = "Select gender",
                                        expanded = genderExpanded,
                                        options = genderOptions,
                                        onToggleExpanded = { genderExpanded = !genderExpanded },
                                        onDismissExpanded = { genderExpanded = false },
                                        onOptionSelected = { gender = it; genderExpanded = false },
                                        error = validationErrors.gender
                                    )
                                }
                            }
                        } else {
                            Column(verticalArrangement = Arrangement.spacedBy(24.dp)) {
                                Row(horizontalArrangement = Arrangement.spacedBy(28.dp)) {
                                    FieldColumn(
                                        modifier = Modifier.weight(1f),
                                        label = "School",
                                        required = true,
                                        field = {
                                            SchoolUserSchoolPopup(
                                                selectedSchool = selectedSchool,
                                                selectedLabel = schoolName.ifBlank { selectedSchool?.name.orEmpty() },
                                                query = schoolQuery,
                                                expanded = schoolExpanded,
                                                filteredSchools = filteredSchools,
                                                onQueryChange = { schoolQuery = it },
                                                onToggleExpanded = { schoolExpanded = !schoolExpanded },
                                                onSchoolSelected = {
                                                    schoolId = it.id
                                                    schoolName = it.name
                                                    schoolQuery = ""
                                                    schoolExpanded = false
                                                },
                                                onDismissExpanded = { schoolExpanded = false },
                                                error = validationErrors.schoolId,
                                                fieldTextColor = AddSchoolUserNavy,
                                                mutedTextColor = AddSchoolUserMuted,
                                                primaryColor = AddSchoolUserPrimary,
                                                borderColor = AddSchoolUserInputBorder,
                                                dangerColor = AddSchoolUserDanger,
                                                surfaceColor = AddSchoolUserSurface,
                                                highlightBorderColor = AddSchoolUserBorder
                                            )
                                        }
                                    )
                                    FieldColumn(
                                        modifier = Modifier.weight(1f),
                                        label = "Full Name",
                                        required = true,
                                        field = {
                                            AddUserTextField(
                                                value = fullName,
                                                onValueChange = { fullName = it },
                                                placeholder = "Enter full name",
                                                error = validationErrors.fullName
                                            )
                                        }
                                    )
                                }
                                Row(horizontalArrangement = Arrangement.spacedBy(28.dp)) {
                                    FieldColumn(
                                        modifier = Modifier.weight(1f),
                                        label = "Staff Role",
                                        required = true,
                                        field = {
                                            AddUserTextField(value = staffRole, onValueChange = { staffRole = it }, placeholder = "Enter staff role", error = validationErrors.staffRole)
                                        }
                                    )
                                    FieldColumn(
                                        modifier = Modifier.weight(1f),
                                        label = "Mobile Number",
                                        required = true,
                                        field = {
                                            AddUserTextField(value = mobileNumber, onValueChange = { mobileNumber = it }, placeholder = "Enter mobile number", error = validationErrors.mobileNumber, keyboardType = androidx.compose.ui.text.input.KeyboardType.Phone)
                                        }
                                    )
                                }
                                Row(horizontalArrangement = Arrangement.spacedBy(28.dp)) {
                                    FieldColumn(
                                        modifier = Modifier.weight(1f),
                                        label = "Email Address",
                                        required = true,
                                        field = {
                                            AddUserTextField(
                                                value = emailAddress,
                                                onValueChange = { emailAddress = it },
                                                placeholder = "Enter email address",
                                                error = validationErrors.emailAddress,
                                                keyboardType = androidx.compose.ui.text.input.KeyboardType.Email
                                            )
                                        }
                                    )
                                    FieldColumn(
                                        modifier = Modifier.weight(1f),
                                        label = "Staff ID",
                                        required = true,
                                        helperText = StaffIdHelperText,
                                        field = {
                                            AddUserTextField(value = staffId, onValueChange = { staffId = it }, placeholder = "Enter staff ID", error = validationErrors.staffId)
                                        }
                                    )
                                }
                                Row(horizontalArrangement = Arrangement.spacedBy(28.dp)) {
                                    FieldColumn(
                                        modifier = Modifier.weight(1f),
                                        label = "Role",
                                        required = true,
                                        field = {
                                            AddUserSelectField(
                                                value = role,
                                                placeholder = "Select role",
                                                expanded = roleExpanded,
                                                options = addSchoolUserRoles,
                                                onToggleExpanded = { roleExpanded = !roleExpanded },
                                                onDismissExpanded = { roleExpanded = false },
                                                onOptionSelected = {
                                                    role = it
                                                    roleExpanded = false
                                                },
                                                error = validationErrors.role
                                            )
                                        }
                                    )
                                    FieldColumn(
                                        modifier = Modifier.weight(1f),
                                        label = "Gender",
                                        required = true,
                                        field = {
                                            AddUserSelectField(value = gender, placeholder = "Select gender", expanded = genderExpanded, options = genderOptions, onToggleExpanded = { genderExpanded = !genderExpanded }, onDismissExpanded = { genderExpanded = false }, onOptionSelected = { gender = it; genderExpanded = false }, error = validationErrors.gender)
                                        }
                                    )
                                }
                                Row(horizontalArrangement = Arrangement.spacedBy(28.dp)) {
                                    Spacer(modifier = Modifier.weight(1f))
                                }
                            }
                        }
                    }
                }
            }
            Footer(
                onClose = { if (!effectiveSaving) onClose() },
                onSave = {
                    hasSubmitted = true
                    val errors = validateAddSchoolUser(currentValues)
                    if (!errors.hasAnyError && canSave) {
                        localSaving = true
                        onSave(currentValues)
                    }
                },
                canSave = canSave,
                isSaving = effectiveSaving
            )
        }
    LaunchedEffect(schoolExpanded) {
        if (!schoolExpanded) {
            schoolQuery = ""
        }
    }

    LaunchedEffect(isSaving) {
        if (!isSaving) {
            localSaving = false
        }
    }

}

@Composable
private fun Header(
    onClose: () -> Unit
) {
    AppDialogHeader(
        title = "Add School User",
        subtitle = "Create a new school-level administrative user.",
        icon = Res.drawable.user_plus,
        closeContentDescription = "Close add school user",
        onClose = onClose,
        colors = AddSchoolUserDialogColors
    )
}

@Composable
private fun Footer(
    onClose: () -> Unit,
    onSave: () -> Unit,
    canSave: Boolean,
    isSaving: Boolean
) {
    AppDialogFooter(
        actions = listOf(
            AppDialogFooterAction(label = "Cancel", onClick = onClose, enabled = !isSaving),
            AppDialogFooterAction(
                label = if (isSaving) "Saving..." else "Save",
                onClick = onSave,
                style = AppDialogActionStyle.Primary,
                enabled = canSave
            )
        ),
        colors = AddSchoolUserDialogColors
    )
}

@Composable
private fun FieldColumn(
    modifier: Modifier = Modifier,
    label: String,
    required: Boolean,
    helperText: String = "",
    field: @Composable () -> Unit
) {
    var helperExpanded by remember { mutableStateOf(false) }
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(horizontalArrangement = Arrangement.spacedBy(4.dp), verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = label,
                color = AddSchoolUserNavy,
                fontSize = AppDialogLabelSize,
                fontWeight = FontWeight.SemiBold
            )
            if (required) {
                Text(
                    text = "*",
                    color = AddSchoolUserDanger,
                    fontSize = AppDialogLabelSize,
                    fontWeight = FontWeight.SemiBold
                )
            }
            if (helperText.isNotBlank()) {
                Box {
                    Icon(
                        painter = painterResource(Res.drawable.info_circle),
                        contentDescription = "Staff ID help",
                        tint = AddSchoolUserPrimary,
                        modifier = Modifier
                            .size(16.dp)
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = { helperExpanded = !helperExpanded }
                            )
                    )
                    DropdownMenu(
                        expanded = helperExpanded,
                        onDismissRequest = { helperExpanded = false },
                        modifier = Modifier
                            .widthIn(max = 280.dp)
                            .background(AddSchoolUserSurface)
                    ) {
                        Text(
                            text = helperText,
                            color = AddSchoolUserMuted,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.Medium,
                            modifier = Modifier.padding(horizontal = 12.dp, vertical = 10.dp)
                        )
                    }
                }
            }
        }
        field()
    }
}

@Composable
private fun AddSchoolUserLabel(
    label: String,
    required: Boolean
) {
    Row(horizontalArrangement = Arrangement.spacedBy(4.dp), verticalAlignment = Alignment.CenterVertically) {
        Text(
            text = label,
            color = AddSchoolUserNavy,
            fontSize = 14.sp,
            fontWeight = FontWeight.ExtraBold
        )
        if (required) {
            Text(
                text = "*",
                color = AddSchoolUserDanger,
                fontSize = 14.sp,
                fontWeight = FontWeight.ExtraBold
            )
        }
    }
}

@Composable
private fun AddUserTextField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    error: String,
    keyboardType: androidx.compose.ui.text.input.KeyboardType = androidx.compose.ui.text.input.KeyboardType.Text
) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        when {
            placeholder.contains("full name", ignoreCase = true) -> AppDialogFullNameField(
                value = value,
                onValueChange = onValueChange,
                placeholder = placeholder,
                error = error,
                colors = AddSchoolUserDialogColors
            )
            keyboardType == androidx.compose.ui.text.input.KeyboardType.Phone -> AppDialogMobileField(
                value = value,
                onValueChange = onValueChange,
                placeholder = placeholder,
                error = error,
                colors = AddSchoolUserDialogColors
            )
            else -> AppDialogTextField(
                value = value,
                onValueChange = onValueChange,
                placeholder = placeholder,
                error = error,
                keyboardType = keyboardType,
                colors = AddSchoolUserDialogColors
            )
        }
        if (error.isNotBlank()) {
            Text(
                text = error,
                color = AddSchoolUserDanger,
                fontSize = 12.sp,
                fontWeight = FontWeight.Medium
            )
        }
    }
}

@Composable
private fun AddUserSelectField(
    value: String,
    placeholder: String,
    expanded: Boolean,
    options: List<String>,
    onToggleExpanded: () -> Unit,
    onDismissExpanded: () -> Unit,
    onOptionSelected: (String) -> Unit,
    error: String,
    showSelectedValueWhenBlank: Boolean = false,
    defaultValue: String = ""
) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        AppDialogSelectField(
            value = when {
                value.isNotBlank() -> value
                showSelectedValueWhenBlank && defaultValue.isNotBlank() -> defaultValue
                else -> ""
            },
            placeholder = placeholder,
            expanded = expanded,
            options = options,
            onToggleExpanded = onToggleExpanded,
            onDismissExpanded = onDismissExpanded,
            onSelected = onOptionSelected,
            error = error,
            colors = AddSchoolUserDialogColors
        )
        if (error.isNotBlank()) {
            Text(
                text = error,
                color = AddSchoolUserDanger,
                fontSize = 13.sp,
                fontWeight = FontWeight.Medium
            )
        }
    }
}

@Composable
private fun AddUserSchoolField(
    selectedSchool: AddSchoolUserSchoolOption?,
    query: String,
    expanded: Boolean,
    filteredSchools: List<AddSchoolUserSchoolOption>,
    onQueryChange: (String) -> Unit,
    onToggleExpanded: () -> Unit,
    onSchoolSelected: (AddSchoolUserSchoolOption) -> Unit,
    error: String
) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(40.dp)
                    .border(
                        1.dp,
                        if (expanded) AddSchoolUserPrimary else if (error.isNotBlank()) AddSchoolUserDanger else AddSchoolUserInputBorder,
                        RoundedCornerShape(8.dp)
                    )
                    .background(AddSchoolUserSurface, RoundedCornerShape(8.dp))
                    .clickable(onClick = onToggleExpanded)
                    .padding(horizontal = 12.dp),
                contentAlignment = Alignment.CenterStart
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        text = selectedSchool?.name ?: "Search and select school",
                        color = if (selectedSchool != null) AddSchoolUserNavy else AddSchoolUserMuted,
                        fontSize = 14.sp,
                        fontWeight = FontWeight.Medium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f)
                    )
                    Icon(
                        painter = painterResource(Res.drawable.chevron_down),
                        contentDescription = null,
                        tint = AddSchoolUserPrimary,
                        modifier = Modifier
                            .size(20.dp)
                            .rotate(if (expanded) 180f else 0f)
                    )
                }
            }
            if (expanded) {
                Surface(
                    modifier = Modifier
                        .fillMaxWidth()
                        .border(1.dp, AddSchoolUserBorder, RoundedCornerShape(8.dp)),
                    shape = RoundedCornerShape(8.dp),
                    color = AddSchoolUserSurface,
                    shadowElevation = 0.dp,
                    tonalElevation = 0.dp,
                    border = androidx.compose.foundation.BorderStroke(1.dp, AddSchoolUserBorder)
                ) {
                    Column {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(56.dp)
                                .border(0.dp, Color.Transparent)
                                .padding(horizontal = 18.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                painter = painterResource(Res.drawable.search),
                                contentDescription = null,
                                tint = AddSchoolUserMuted,
                                modifier = Modifier.size(22.dp)
                            )
                            Spacer(modifier = Modifier.size(10.dp))
                            BasicTextField(
                                value = query,
                                onValueChange = onQueryChange,
                                singleLine = true,
                                textStyle = TextStyle(
                                    fontSize = 14.sp,
                                    lineHeight = 18.sp,
                                    fontWeight = FontWeight.Medium,
                                    color = AddSchoolUserNavy
                                ),
                                cursorBrush = SolidColor(com.schoolconnect.app.designsystem.AppPrimary),
                                modifier = Modifier.fillMaxWidth()
                            ) { innerTextField ->
                                Box(
                                    modifier = Modifier.fillMaxWidth(),
                                    contentAlignment = Alignment.CenterStart
                                ) {
                                    if (query.isBlank()) {
                                        Text(
                                            text = "Search school...",
                                            color = AddSchoolUserMuted,
                                            fontSize = 14.sp,
                                            fontWeight = FontWeight.Medium
                                        )
                                    }
                                    innerTextField()
                                }
                            }
                        }
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .heightIn(max = 240.dp)
                                .verticalScroll(rememberScrollState())
                                .padding(vertical = 10.dp)
                        ) {
                            filteredSchools.forEach { school ->
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height(36.dp)
                                        .clickable(onClick = { onSchoolSelected(school) })
                                        .padding(horizontal = 18.dp),
                                    contentAlignment = Alignment.CenterStart
                                ) {
                                    Text(
                                        text = school.name,
                                        color = AddSchoolUserNavy,
                                        fontSize = 14.sp,
                                        fontWeight = FontWeight.Medium,
                                        maxLines = 1,
                                        overflow = TextOverflow.Ellipsis
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
        if (error.isNotBlank()) {
            Text(
                text = error,
                color = AddSchoolUserDanger,
                fontSize = 13.sp,
                fontWeight = FontWeight.Medium
            )
        }
    }
}
